import { ALGN_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/algn';
import { FLIP_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/flip';
import { IImageFill, IImageTile, ImageTileFlipType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import {
    alphaToEmu, canvasToEmu, emuToAlpha,
    emuToCanvas, emuToPercent, percentToEmu,
} from '@src/slide/infrastructure/module/independent/unit';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { ICTBlipFillProperties, STRectAlignment, STTileFlipMode } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import _set from 'lodash/set';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';

/**
 * 图片填充
 */
export class BlipFillConverter {
    /**
     * 渐变填充的sdk -> ui 映射
     * @param sdkData
     */
    public static convertPartialDataFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>): IImageFill {
        const uiData: IImageFill = {
            src: BlipFillConverter.convertSrcFromSdkToUi(sdkData) || '',
            stretch: BlipFillConverter.convertStretchFromSdkToUi(sdkData),
            tile: BlipFillConverter.convertTileFromSdkToUi(sdkData),
            alpha: BlipFillConverter.convertAlphaFromSdkToUi(sdkData),
            rotWithShape: BlipFillConverter.convertRotWithShapeFromSdkToUi(sdkData),
        };
        // 清掉值是undefined的属性
        Object.keys(uiData).forEach((p) => {
            if (uiData[p] === undefined) {
                delete uiData[p];
            }
        });
        return uiData;
    }

    /**
     * 渐变填充的ui -> sdk 映射
     * @param uiData
     */
    public static convertPartialDataFromUiToSdk(uiData:
    DeepReadonly<DeepPartial<IImageFill>>): ICTBlipFillProperties {
        const sdkData: ICTBlipFillProperties = {};
        // src
        BlipFillConverter.convertSrcFromUiToSdk(uiData, sdkData);
        // stretch
        BlipFillConverter.convertStretchFromUiToSdk(uiData, sdkData);
        // tile
        BlipFillConverter.convertTileFromUiToSdk(uiData, sdkData);
        // alpha
        BlipFillConverter.convertAlphaFromUiToSdk(uiData, sdkData);
        // rotWithShape
        BlipFillConverter.convertRotWithShapeFromUiToSdk(uiData, sdkData);
        return sdkData;
    }

    // 充满
    public static convertStretchFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>):
        { fillRect?: { b?: number, t?: number, l?: number, r?: number } } | undefined {
        const stretch = sdkData.egFillModeProperties?.stretch;
        const fillRect = stretch?.fillRect;
        if (fillRect) {
            const uiFillRect = {};
            Object.keys(fillRect).forEach((p) => {
                uiFillRect[p] = fillRect[p] / 100000 * -1;
            });
            return { fillRect: uiFillRect };
        } if (stretch) {
            return {};
        }
    }

    // 平铺
    public static convertTileFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>): IImageTile | undefined {
        const tile = sdkData.egFillModeProperties?.tile;
        if (tile) {
            return {
                tx: tile.tx ? emuToCanvas(tile.tx) : 0, // 图片偏移量
                ty: tile.ty ? emuToCanvas(tile.ty) : 0,
                sx: typeof tile.sx === 'number' ? emuToPercent(tile.sx) : 100, // 图片百分比
                sy: typeof tile.sy === 'number' ? emuToPercent(tile.sy) : 100,
                flip: FLIP_VER3_MAP.get(tile.flip || STTileFlipMode.none)!, // 图片镜像，值有 none,xy,x,y
                algn: ALGN_VER3_MAP.get(tile.algn || STRectAlignment?.tl)!, // 对齐方式，tl,tr,bl,br,t,l,ctr,r,b
            };
        }
    }

    // src
    private static convertSrcFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>): string | undefined {
        const sdkSrc = sdkData?.blip?.embed;
        const { src, forceHidden } = ImageQuickLookManager.getInstance().getNextUIDataFromSDKSrc(sdkSrc);
        return forceHidden ? undefined : src;
    }

    // alpha
    private static convertAlphaFromSdkToUi(sdkData:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>): number | undefined {
        const alpha = sdkData.blip?.alphaModFix?.[0]?.amt;
        if (alpha || alpha === 0) {
            return emuToAlpha(alpha);
        }
    }

    // rotWithShape
    private static convertRotWithShapeFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTBlipFillProperties>>): 1 | 0 | undefined {
        if (sdkData?.rotWithShape === true || sdkData?.rotWithShape === false) {
            return sdkData.rotWithShape ? 1 : 0;
        }
    }

    // src
    private static convertSrcFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IImageFill>>,
        sdkData: ICTBlipFillProperties,
    ): void {
        if (uiData.src) {
            const resource = ImageQuickLookManager.getInstance().getResourceByUrl(uiData.src);
            _set(sdkData, 'blip.embed', resource ? resource.id : uiData.src);
        }
    }

    // 充满
    private static convertStretchFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IImageFill>>,
        sdkData: ICTBlipFillProperties,
    ): void {
        if (uiData.stretch?.fillRect) {
            const sdkFillRect = {};
            Object.keys(uiData.stretch.fillRect).forEach((p) => {
                const val = uiData.stretch?.fillRect?.[p];
                if (val || val === 0) {
                    sdkFillRect[p] = val * 100000 * -1;
                }
            });
            _set(sdkData, 'egFillModeProperties.stretch.fillRect', sdkFillRect);
        } else if (uiData.stretch?.fillRect === null) {
            _set(sdkData, 'egFillModeProperties.stretch', null);
        } else if (!uiData.tile) {
            // 不是平铺的话要保证stretch节点有值，否则导出到office缩放会有问题
            _set(sdkData, 'egFillModeProperties.stretch', {});
        } else {
            _set(sdkData, 'egFillModeProperties.stretch', null);
        }
    }

    // 平铺
    private static convertTileFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IImageFill>>,
        sdkData: ICTBlipFillProperties,
    ): void {
        if (uiData.tile) {
            _set(sdkData, 'egFillModeProperties.tile', {
                tx: uiData.tile.tx ? canvasToEmu(uiData.tile.tx) : 0, // 图片偏移量
                ty: uiData.tile.ty ? canvasToEmu(uiData.tile.ty) : 0,
                sx: uiData.tile.sx ? percentToEmu(uiData.tile.sx) : percentToEmu(100), // 图片百分比
                sy: uiData.tile.sy ? percentToEmu(uiData.tile.sy) : percentToEmu(100),
                flip: invertMap(FLIP_VER3_MAP).get(uiData.tile.flip || ImageTileFlipType.NONE), // 图片镜像，值有 none,xy,x,y
                algn: invertMap(ALGN_VER3_MAP).get(uiData.tile.algn || EEffectAlgn.TL), // 对齐方式，tl,tr,bl,br,t,l,ctr,r,b
            });
        } else {
            _set(sdkData, 'egFillModeProperties.tile', null);
        }
    }

    // alpha
    private static convertAlphaFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IImageFill>>,
        sdkData: ICTBlipFillProperties,
    ): void {
        if (uiData.alpha || uiData.alpha === 0) {
            _set(sdkData, 'blip.alphaModFix[0].amt', alphaToEmu(uiData.alpha));
        }
    }

    // rotWithShape
    private static convertRotWithShapeFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IImageFill>>,
        sdkData: ICTBlipFillProperties,
    ): void {
        if (uiData.rotWithShape === 1 || uiData.rotWithShape === 0) {
            sdkData.rotWithShape = !!uiData.rotWithShape;
        }
    }
}
